import { Game } from "./engine";
import { Cell, DIRS, Dir } from "./types";

const DIRK = Object.keys(DIRS) as Dir[];
type P = { x: number; y: number };
const key = (x: number, y: number) => y * 100 + x;

// Simple autoplayer used by the trailer mode and offline simulations.
export function createBot(g: Game) {
  let plan: { target: P; dir: Dir } | null = null;
  let idle = 0;
  const s = () => g.state as any;
  const free = (x: number, y: number) => { const st = s(); return x >= 0 && y >= 0 && x < st.cols && y < st.rows && st.grid[y][x] === Cell.Floor && !st.sliders.some((sl: any) => (sl.x === x && sl.y === y) || (sl.tx === x && sl.ty === y)); };
  const dangerAt = (x: number, y: number) => { let d = 99; for (const e of s().enemies) { if (e.frozen > 0) continue; const dd = Math.abs(e.tx - x) + Math.abs(e.ty - y); if (dd < d) d = dd; } return d; };
  const bfs = (sx: number, sy: number) => {
    const prev = new Map<number, number>(); prev.set(key(sx, sy), -1);
    const q: P[] = [{ x: sx, y: sy }];
    while (q.length) {
      const c = q.shift()!;
      for (const d of DIRK) {
        const nx = c.x + DIRS[d].dx, ny = c.y + DIRS[d].dy;
        if (!free(nx, ny) || prev.has(key(nx, ny)) || dangerAt(nx, ny) <= 1) continue;
        prev.set(key(nx, ny), key(c.x, c.y)); q.push({ x: nx, y: ny });
      }
    }
    return prev;
  };
  const firstStep = (prev: Map<number, number>, sx: number, sy: number, tx: number, ty: number): Dir | null => {
    let k = key(tx, ty);
    if (!prev.has(k)) return null;
    while (prev.get(k) !== key(sx, sy) && prev.get(k) !== -1) k = prev.get(k)!;
    if (prev.get(k) === -1) return null;
    const lx = k % 100, ly = Math.floor(k / 100);
    for (const d of DIRK) if (sx + DIRS[d].dx === lx && sy + DIRS[d].dy === ly) return d;
    return null;
  };
  const pushHits = (bx: number, by: number, d: Dir) => {
    const st = s();
    const en = st.enemies.filter((e: any) => !e.flying && e.frozen <= 0).map((e: any) => ({ x: e.tx, y: e.ty }));
    let x = bx + DIRS[d].dx, y = by + DIRS[d].dy, steps = 0;
    while (x >= 0 && y >= 0 && x < st.cols && y < st.rows && st.grid[y][x] === Cell.Floor) {
      if (en.some((e: P) => e.x === x && e.y === y)) return steps < 8;
      x += DIRS[d].dx; y += DIRS[d].dy; steps++;
    }
    return false;
  };
  const isBall = (x: number, y: number) => { const st = s(); const k = st.kinds[y]?.[x]; return st.grid[y]?.[x] === Cell.Crystal && k !== "diamond" && k !== "diamond2" && k !== "bomb"; };

  return {
    step() {
      const st = s();
      const p = st.player;
      if (st.phase !== "playing" || p.deadTimer > 0 || p.t < 1 || st.introHold > 0) return;
      if (dangerAt(p.x, p.y) <= 1) {
        let best: Dir | null = null, bd = -1;
        for (const d of DIRK) { const nx = p.x + DIRS[d].dx, ny = p.y + DIRS[d].dy; if (!free(nx, ny)) continue; const dd = dangerAt(nx, ny); if (dd > bd) { bd = dd; best = d; } }
        g.setHeld(best); g.update(1); g.setHeld(null); plan = null; return;
      }
      if (plan && p.x === plan.target.x && p.y === plan.target.y) {
        const bx = p.x + DIRS[plan.dir].dx, by = p.y + DIRS[plan.dir].dy;
        if (isBall(bx, by)) { p.dir = plan.dir; g.pushAction(); g.update(1); }
        plan = null; return;
      }
      if (!plan || idle > 90) {
        idle = 0;
        const prev = bfs(p.x, p.y);
        let best: { target: P; dir: Dir; dist: number } | null = null;
        for (let y = 0; y < st.rows; y++) for (let x = 0; x < st.cols; x++) {
          if (!isBall(x, y)) continue;
          for (const d of DIRK) {
            const sx = x - DIRS[d].dx, sy = y - DIRS[d].dy;
            if (!prev.has(key(sx, sy)) || !pushHits(x, y, d)) continue;
            const dist = Math.abs(sx - p.x) + Math.abs(sy - p.y);
            if (!best || dist < best.dist) best = { target: { x: sx, y: sy }, dir: d, dist };
          }
        }
        if (best) plan = { target: best.target, dir: best.dir };
        else {
          const cells = [...prev.keys()].filter((k) => dangerAt(k % 100, Math.floor(k / 100)) >= 3);
          if (cells.length) { const k = cells[Math.floor(Math.random() * cells.length)]; plan = { target: { x: k % 100, y: Math.floor(k / 100) }, dir: "up" }; }
        }
      }
      if (plan) {
        const d = firstStep(bfs(p.x, p.y), p.x, p.y, plan.target.x, plan.target.y);
        if (!d) { plan = null; idle++; return; }
        g.setHeld(d); g.update(1); g.setHeld(null);
      } else idle++;
    },
  };
}
